import React, { forwardRef, useEffect, useImperativeHandle } from 'react';
import { useNavigate } from 'react-router-dom';

import { getFaxUser } from 'api/Fax';

import { ia } from 'lib/helpers/utility';

import Icon from 'components/shared/Icon/Icon';
import Input from 'components/shared/Input/Input';
import NewTagSelect from 'components/shared/Tags/components/Select/NewSelect';
import Textarea from 'components/shared/Textarea/Textarea';

import { formatNumber } from '../lib';

const SendFaxForm = forwardRef(
  ({ formik, setContactsModalVisible, setFax_user, resetTags, error, setError, fax }, ref) => {
    const { setFieldValue, values, handleChange } = formik;

    const navigate = useNavigate;

    useImperativeHandle(ref, () => ({
      submitForm: (type) => {
        if (formik.isSubmitting) {
          return;
        }
        setFieldValue('type', type).then(() => {
          if (type === 'reset') {
            formik.resetForm();
            return;
          }

          const shouldSend =
            values.fax_numbers.some((item) => item.fax_number !== '') ||
            (ia(values.contacts) && ia(values.files));

          if (type === 'send' && shouldSend) {
            formik.submitForm();
            return;
          }

          if (type === 'draft') {
            formik.submitForm();
            return;
          }

          if (
            (values.fax_numbers.some((item) => item.fax_number !== '') ||
              ia(values.contacts) ||
              type === 'preview') &&
            ia(values.files)
          ) {
            setFieldValue('type', type).then(() => {
              formik.submitForm();
            });
            return;
          }

          setError((prev) => ({
            ...prev,
            [!ia(values.files) ? 'file' : 'number']: `At least one ${
              !ia(values.files) ? 'file' : 'number'
            } is required`
          }));
        });
      }
    }));

    const getCurrentFaxUser = async () => {
      const { fax_user } = await getFaxUser(navigate);
      setFax_user(fax_user);
    };
    useEffect(() => {
      getCurrentFaxUser();
    }, []);

    const handleChangeFaxNumber = (e, index) => {
      setError((prev) => ({ ...prev, number: null }));
      setFieldValue(`fax_numbers[${index}].fax_number`, e.target.value);
    };

    const handleClearContact = (contact) => {
      setFieldValue(
        'contacts',
        values.contacts.filter((item) => item.id !== contact.id)
      );
    };

    return (
      <div className="flex-1 !p-6">
        <div className="flex items-center justify-between">
          <label htmlFor="">Recipient number</label>
          <label
            htmlFor=""
            className="cursor-pointer flex gap-1 text-xs text-neutral-600"
            onClick={() => setContactsModalVisible(true)}>
            Search from contacts
            <Icon
              icon="user-square-mono"
              color="primary"
              className="inline cursor-pointer"
              data-qa="search-from-contacts"
            />
          </label>
        </div>
        <div className="flex w-full flex-col gap-2">
          {values.contacts.map((contact) => (
            <div
              key={contact.id}
              className="mt-2 flex min-h-[40px] items-center justify-between rounded-md border border-solid border-neutral-200 bg-white !px-3 !py-2">
              <p>{contact.first_name + ' ' + contact.last_name}</p>
              <Icon
                icon="close-linear"
                onClick={() => handleClearContact(contact)}
                className="mt-1"
              />
            </div>
          ))}
        </div>
        {!ia(formik.values.contacts) &&
          formik.values.fax_numbers.map(({ fax_number }, index) => (
            <Input
              data-qa="fax-number"
              key={index}
              required
              placeholder="(500) 234-5689"
              hideNumberArrows
              onChange={(e) => handleChangeFaxNumber(e, index)}
              name="fax_number"
              value={fax_number}
              onBlur={() => {
                formatNumber(fax_number, index, setFieldValue);
              }}
              className="!mt-2"
            />
          ))}

        {error.number && <p className="!pt-2 text-sm text-danger-500">{error.number}</p>}

        <div className="!mt-6 flex flex-col gap-4">
          <Input
            name="deliver_to"
            label="Deliver to"
            onChange={handleChange}
            data-qa="deliver-to"
            value={values?.deliver_to}
          />
          <Input
            data-qa="enter-subject"
            placeholder="Enter subject"
            name="subject"
            label="Subject"
            onChange={handleChange}
            value={values?.subject}
          />
          <div className="flex flex-col gap-y-1">
            <p className="text-sm font-500">Tags</p>
            <NewTagSelect
              kind="fax"
              menuPortalTarget={document.body}
              currentTags={fax?.tag_ids || []}
              setTagIds={(ids) => formik.setFieldValue('tag_ids', ids)}
              resetTags={resetTags}
              emptyStateText="Select tags"
              outlined
            />
          </div>

          <Textarea
            data-qa="enter-efax-note"
            placeholder="Enter eFax note"
            label="Note"
            name="notes"
            onChange={handleChange}
            textareaClassName="!min-h-[120px]"
            value={values?.notes}
            maxLength={255}
          />
        </div>
      </div>
    );
  }
);

export default SendFaxForm;
